import { getIdTokenResult, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { AuthUser, UserClaims, UserStatus, UserType } from 'interfaces/user';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { logout, resetStorage, resetToken } from 'services/auth';
import { User, auth } from 'services/firebase';
import { logError } from 'services/logging';
import { NILE_ADMIN_PROFILE_ID } from 'services/theming';
import { useGraphState } from 'components/GraphProvider';
import { recordAuthEvent } from 'utils/auth-instrumentation';
import setActiveTimeout from 'utils/setActiveTimeout';
import withTimeout from 'utils/withTimeout';
import { Role } from 'generated/graphql';
import useOnlineStatus, { OnlineStatus } from './useOnlineStatus';
import { useSnackbar } from './useSnackBar';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const maxRetries = 10;
const retryDelay = 500;
const AUTH_TOKEN_TIMEOUT_MS = 15_000;
const AUTH_CALLBACK_TIMEOUT_MS = 20_000;
// Firebase calls onAuthStateChanged for a stored user only after its accounts:lookup round trip,
// which it gives up on after 30s (60s on mobile). Until then a slow network is not a stall.
const AUTH_CALLBACK_STALL_MS = 65_000;
const AUTH_INIT_RETRY_MESSAGE = 'This is taking longer than usual';
// A forced refresh is a token round trip Firebase rate-limits (auth/quota-exceeded), and a tab
// switch fires focus and visibilitychange together, so refreshes on return are spaced out.
const FOCUS_REFRESH_INTERVAL_MS = 5 * 60_000;

// Ask the boot splash to keep showing and offer Reload when auth init stalls,
// instead of setting isInitialised and leaving the user on a blank screen.
const showAuthInitRetry = () => {
  window.dispatchEvent(
    new CustomEvent('app-loader-retry', {
      detail: { message: AUTH_INIT_RETRY_MESSAGE },
    }),
  );
};

export const getUserFromParsedIdToken = (parsedToken: any): AuthUser => {
  const claims = parsedToken?.claims as UserClaims;

  return {
    name: claims.name,
    email: claims.email,
    userType: claims.userType,
    profileId: claims.profileId,
    adminProfileId: claims.admin_profile_id ?? NILE_ADMIN_PROFILE_ID,
    hubId: claims.hubId,
    userId: claims.user_id,
    theme: claims.theme,
    status: claims.status,
    intercomUserHash: claims.intercomUserHash,
    multiProfile: claims.multiProfile,
    roles: claims.roles,
    emailVerified: claims.email_verified,
  };
};

export interface IUser {
  user: AuthUser | null;
  setUser: Dispatch<SetStateAction<AuthUser | null>>;
  isAdmin: boolean;
  isNileUser: boolean;
  isSeller: boolean;
  isBuyer: boolean;
  isRegistering: boolean;
  isInitialised: boolean;
  isPendingReview: boolean;
  onLogout: () => Promise<void>;
  onRefreshUser: () => Promise<AuthUser | undefined>;
  hasRole: (role: Role) => boolean;
  onValidateUser: () => Promise<void>;
}

export const UserContext = createContext<null | IUser>(null);

const handleResetTokenStorage = async () => {
  await resetToken();
  await resetStorage();
};

export const UserProvider = ({ children, initial = null }: { children: ReactNode; initial?: null | AuthUser }) => {
  const [isInitialised, setIsInitialised] = useState(false);
  const [user, setUser] = useState<null | AuthUser>(initial);
  const { resetClient } = useGraphState();
  const isNileUser = user?.adminProfileId === NILE_ADMIN_PROFILE_ID;
  const isRegistering = user?.status === undefined || user?.status === UserStatus.Registering;
  const isPendingReview = !isRegistering && user?.status !== UserStatus.Approved;
  const { showSnackbar } = useSnackbar();
  const onlineStatus = useOnlineStatus();
  const isOnline = useMemo(() => onlineStatus !== OnlineStatus.Offline, [onlineStatus]);
  const lastFocusRefreshAt = useRef(0);

  const handleResetUserClientState = useCallback(() => {
    resetClient();
    setUser(null);
  }, [resetClient]);

  const onTokenChange = useCallback(
    async (tokenUser: User | null) => {
      try {
        if (tokenUser) {
          const result = await withTimeout(
            getIdTokenResult(tokenUser),
            AUTH_TOKEN_TIMEOUT_MS,
            'getIdTokenResult during auth init',
          );
          setUser(getUserFromParsedIdToken(result));
        } else if (isOnline) {
          // Only reset when online
          recordAuthEvent('session-reset', {
            source: 'useUser.onTokenChange',
            reason: 'firebase reported null user while online',
          });
          await handleResetTokenStorage();
          handleResetUserClientState();
        }
        // Don't log out when offline - preserve user state
        setIsInitialised(true);
      } catch (error) {
        logError(error, { context: 'onTokenChange', message: 'Auth initialisation failed' });
        // Keep splash up with Reload so the user can retry instead of hanging or landing in a blank state.
        showAuthInitRetry();
      }
    },
    [handleResetUserClientState, isOnline],
  );

  const handleLogout = useCallback(async () => {
    await logout(true, showSnackbar, {
      source: 'useUser.handleLogout',
      reason: 'user initiated logout',
    });
    handleResetUserClientState();
  }, [showSnackbar, handleResetUserClientState]);

  const handleRefreshUser = useCallback(async () => {
    try {
      if (!auth.currentUser) {
        if (user) {
          recordAuthEvent('session-reset', {
            source: 'useUser.handleRefreshUser',
            reason: 'auth.currentUser null while app user set',
          });
          await handleResetTokenStorage();
          handleResetUserClientState();
        }
        return;
      }
      const authUser = await getIdTokenResult(auth.currentUser, isOnline);
      const parsedUser = getUserFromParsedIdToken(authUser);
      setUser(parsedUser);
      return parsedUser;
    } catch (err: any) {
      logError(err);
    }
  }, [isOnline, handleResetUserClientState, user]);

  const handleValidateUser = useCallback(async () => {
    if (!isOnline) {
      // validation is only on Google signup/signin so no need to do when offline
      throw new Error('Please connect to the internet to validate your account.');
    }
    for (let retries = 0; retries < maxRetries; retries++) {
      // Check the user this refresh parsed, not `user`: that is whatever this callback closed over
      // when it was created, which after a sign-in redirect is still the signed-out null.
      const refreshedUser = await handleRefreshUser();
      if (refreshedUser?.status === UserStatus.Rejected) {
        throw new Error('Your profile is not active. Please contact support for more information.');
      }
      if (refreshedUser?.status !== undefined) {
        return;
      }
      if (retries < maxRetries - 1) {
        await delay(retryDelay);
      }
    }
    throw new Error('Failed to retrieve user status after multiple attempts. Please check your internet connection.');
  }, [isOnline, handleRefreshUser]);

  useEffect(() => {
    onAuthStateChanged(auth, onTokenChange);
    onIdTokenChanged(auth, onTokenChange);
  }, [onTokenChange]);

  // If Firebase is slow to invoke onAuthStateChanged, offer Reload on the splash, and report it once
  // it is past Firebase's own network timeout (e.g. stalled IndexedDB on iOS).
  useEffect(() => {
    if (isInitialised) return;

    const cancelRetryPrompt = setActiveTimeout(showAuthInitRetry, AUTH_CALLBACK_TIMEOUT_MS);
    const cancelStallReport = setActiveTimeout(() => {
      logError(new Error('Firebase auth initialisation timed out waiting for onAuthStateChanged'));
    }, AUTH_CALLBACK_STALL_MS);

    return () => {
      cancelRetryPrompt();
      cancelStallReport();
    };
  }, [isInitialised]);

  // Only refresh once the tab is back in front. Refreshing on the way out spends a forced token
  // round trip nobody is waiting for, and it races the unload: `visibilitychange` -> hidden fires
  // before `pagehide`, and firebase/auth closes its IndexedDB connection on `pagehide`, so a
  // refresh started while hiding persists its new token against a closed database and rejects
  // with "Database is closing".
  const handleFocusRefresh = useCallback(() => {
    if (Date.now() - lastFocusRefreshAt.current < FOCUS_REFRESH_INTERVAL_MS) return;
    lastFocusRefreshAt.current = Date.now();
    void handleRefreshUser();
  }, [handleRefreshUser]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    handleFocusRefresh();
  }, [handleFocusRefresh]);

  useEffect(() => {
    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleFocusRefresh, handleVisibilityChange]);

  const value = useMemo(() => {
    const hasRole = (role: Role) => !!user?.roles?.includes(role);

    return {
      user,
      setUser,
      isRegistering,
      isPendingReview,
      isAdmin: user?.userType === UserType.Admin,
      isSeller: user?.userType === UserType.Seller,
      isBuyer: user?.userType === UserType.Buyer,
      isNileUser,
      onLogout: handleLogout,
      onRefreshUser: handleRefreshUser,
      isInitialised,
      hasRole,
      onValidateUser: handleValidateUser,
    };
  }, [
    user,
    setUser,
    handleLogout,
    isRegistering,
    isPendingReview,
    handleRefreshUser,
    isNileUser,
    isInitialised,
    handleValidateUser,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useUser = () => {
  const result = useContext(UserContext);
  if (!result) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return result;
};

export default useUser;
